import ApiUrlCreatorService from "@core-ui/ContextServices/ApiUrlCreator";
import { useCatalogPropsStore } from "@core-ui/stores/CatalogPropsStore/CatalogPropsStore.provider";
import type { ViewSettings } from "@ext/markdown/elements/view/models/ViewSettings";
import ViewRenderContent from "@ext/markdown/elements/view/render/components/ViewRenderContent";
import { useViewRenderData } from "@ext/markdown/elements/view/render/logic/hooks/useViewRenderData";
import type { Property } from "@ext/properties/models";
import { type ReactNode, useMemo } from "react";

interface ViewProps extends ViewSettings {
	disabled?: boolean;
	commentId?: string;
	isPrint?: boolean;
	className?: string;
	/** Shown while the catalog data is still being fetched. */
	loading?: ReactNode;
	updateArticle?: (
		articlePath: string,
		property: string,
		value: string,
		isDelete?: boolean,
		moveFrom?: string | null,
	) => void;
}

// A property deleted from the catalog stays in the `select`/`groupby` of the views that named it.
// Read mode, print and portal pass `select`/`groupby` through as parsed, so either may be missing.
const useCatalogPropertyIds = (ids: string[] | undefined, catalogProperties: Property[] | undefined) =>
	useMemo(() => {
		if (!ids || !catalogProperties) return ids;
		return ids.filter((id) => catalogProperties.some((property) => property.id === id));
	}, [ids, catalogProperties]);

const View = (props: ViewProps) => {
	const {
		defs,
		orderby,
		display,
		updateArticle,
		disabled = true,
		commentId,
		isPrint,
		className,
		loading = null,
	} = props;
	const apiUrlCreator = ApiUrlCreatorService.value;
	const catalogProperties = useCatalogPropsStore((state) => state.data?.properties);
	const groupby = useCatalogPropertyIds(props.groupby, catalogProperties);
	const select = useCatalogPropertyIds(props.select, catalogProperties);
	const content = useViewRenderData({
		url: apiUrlCreator?.getViewRenderData(),
		defs,
		orderby,
		groupby,
		select,
		display,
	});

	if (!content) return <>{loading}</>;
	return (
		<ViewRenderContent
			className={className}
			commentId={commentId}
			content={content}
			defs={defs}
			disabled={disabled}
			display={display}
			groupby={groupby}
			isPrint={isPrint}
			orderby={orderby}
			select={select}
			updateArticle={updateArticle}
		/>
	);
};

export default View;
